import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhDotsThreeVertical";
import "@phosphor-icons/webcomponents/PhNotePencil";
import "@phosphor-icons/webcomponents/PhEye";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhArchive";
import "ui-components/button/wow-button";
import "ui-components/dropdown-menu/dropdown-menu";
import "ui-components/dropdown-menu/dropdown-menu-item";
import "../message-status-badge/message-status-badge";

import type { CreatorMessage } from "api-contracts/rpc/get-creator-messages";
import { t } from "../../translations/i18n";
import { getLang } from "ui-common/i18n";
import { styles } from "./recent-message-banner.css";

import type { MessageStatus } from "../message-status-badge/message-status-badge";

@customElement("recent-message-banner")
export class RecentMessageBanner extends LitElement {
  static override styles = [styles];

  @property({ type: Object })
  message!: CreatorMessage;

  private emit(name: string, detail?: unknown) {
    this.dispatchEvent(new CustomEvent(name, { detail, bubbles: true, composed: true }));
  }

  private renderStatusBadge() {
    return html`<message-status-badge .status=${this.message.status as MessageStatus}></message-status-badge>`;
  }

  private getStatusDateInfo() {
    const s = this.message.status as MessageStatus;

    if (s === "sent" || s === "sending") {
      return { dateStr: this.message.sent_at, prefixKey: "messagesPage.sentAt" as const };
    }
    if (s === "scheduled") {
      return { dateStr: this.message.scheduled_at, prefixKey: "messagesPage.scheduledAt" as const };
    }
    return { dateStr: this.message.created, prefixKey: "messagesPage.createdAt" as const };
  }

  private formatDateValue(dateStr: string): string {
    const date = new Date(dateStr);
    const locale = getLang();
    return `${date.toLocaleDateString(locale, { dateStyle: "long" }).toLowerCase()}, ${date.toLocaleTimeString(locale, { timeStyle: "short" })}`;
  }

  private renderStats() {
    const s = this.message.status as MessageStatus;
    if (s !== "sent" && s !== "sending") return nothing;

    return html`
      <div class="banner-stats">
        <div class="banner-stat">
          <span class="stat-label">${t("messagesPage.columnDelivered")}</span>
          <span class="stat-value">${this.message.delivered_count}</span>
        </div>
        <div class="banner-stat">
          <span class="stat-label">${t("messagesPage.columnOpens")}</span>
          <span class="stat-value">${this.message.opened_count}</span>
        </div>
        <div class="banner-stat">
          <span class="stat-label">${t("messagesPage.columnClicks")}</span>
          <span class="stat-value">${this.message.clicked_count}</span>
        </div>
      </div>
    `;
  }

  private renderActionButton() {
    const s = this.message.status as MessageStatus;

    if (s === "draft" || s === "scheduled") {
      return html`
        <wow-button
          variant="neutral"
          size="small"
          rounded
          @click=${() => this.emit("edit-message", { message: this.message })}
        >
          <ph-note-pencil slot="prefix" size="16"></ph-note-pencil>
          ${t("messagesPage.editMessage")}
        </wow-button>
      `;
    }

    if (s === "sent" || s === "sending" || s === "archived") {
      return html`
        <wow-button
          variant="neutral"
          size="small"
          rounded
          @click=${() => this.emit("view-message", { message: this.message })}
        >
          <ph-eye slot="prefix" size="16"></ph-eye>
          ${t("messagesPage.viewMessage")}
        </wow-button>
      `;
    }

    return nothing;
  }

  private renderMenu() {
    const s = this.message.status as MessageStatus;

    if (s === "draft" || s === "scheduled") {
      return this.renderDropdownMenu([
        { action: "duplicate", icon: "ph-copy", label: "messagesPage.duplicateMessage" as const },
        { action: "edit", icon: "ph-note-pencil", label: "messagesPage.editMessage" as const },
        { action: "archive", icon: "ph-archive", label: "messagesPage.archiveMessage" as const },
      ]);
    }

    if (s === "sent" || s === "sending") {
      return this.renderDropdownMenu([
        { action: "duplicate", icon: "ph-copy", label: "messagesPage.duplicateMessage" as const },
        { action: "archive", icon: "ph-archive", label: "messagesPage.archiveMessage" as const },
      ]);
    }

    if (s === "archived") {
      return this.renderDropdownMenu([
        { action: "duplicate", icon: "ph-copy", label: "messagesPage.duplicateMessage" as const },
      ]);
    }

    return nothing;
  }

  private renderDropdownMenu(items: { action: string; icon: string; label: Parameters<typeof t>[0] }[]) {
    return html`
      <dropdown-menu glass placement="bottom-end" @dropdown-item-click=${this.handleMenuAction}>
        <wow-button
          slot="trigger"
          variant="subtle"
          size="medium"
          rounded
          .borderColor=${"var(--sds-color-border-default-secondary)"}
          aria-label="More options"
        >
          <ph-dots-three-vertical size="20"></ph-dots-three-vertical>
        </wow-button>
        ${items.map(
          (item) => html`
            <dropdown-menu-item action=${item.action} icon-position="start">
              ${this.renderMenuIcon(item.icon)} ${t(item.label)}
            </dropdown-menu-item>
          `,
        )}
      </dropdown-menu>
    `;
  }

  private renderMenuIcon(icon: string) {
    switch (icon) {
      case "ph-copy":
        return html`<ph-copy slot="icon" size="16"></ph-copy>`;
      case "ph-note-pencil":
        return html`<ph-note-pencil slot="icon" size="16"></ph-note-pencil>`;
      case "ph-archive":
        return html`<ph-archive slot="icon" size="16"></ph-archive>`;
      default:
        return nothing;
    }
  }

  private handleMenuAction = (e: CustomEvent) => {
    const action = e.detail?.action;
    switch (action) {
      case "edit":
        this.emit("edit-message", { message: this.message });
        break;
      case "view":
        this.emit("view-message", { message: this.message });
        break;
      case "duplicate":
        this.emit("duplicate-message", { message: this.message });
        break;
      case "archive":
        this.emit("archive-message", { messageId: this.message.id });
        break;
    }
  };

  override render() {
    if (!this.message) return nothing;

    return html`
      <div class="recent-message-banner">
        <div class="banner-content">
          <div class="banner-header">
            <h3>${this.message.title}</h3>
          </div>
          <p class="banner-preview">${this.message.content}</p>
          <div class="banner-bottom">
            <div class="banner-info">
              <div class="banner-date-row">
                ${this.renderStatusBadge()}
                <span class="banner-date"
                  >${t(this.getStatusDateInfo().prefixKey)}${this.getStatusDateInfo().dateStr
                    ? `, ${this.formatDateValue(this.getStatusDateInfo().dateStr!)}`
                    : nothing}</span
                >
              </div>
              ${this.renderStats()}
            </div>
            <div class="banner-actions">${this.renderActionButton()} ${this.renderMenu()}</div>
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "recent-message-banner": RecentMessageBanner;
  }
}
